import React, { useState, useEffect, useRef } from 'react';
import { AlertTriangle, HelpCircle, Info } from 'lucide-react';

/**
 * In-app replacement for window.confirm / window.prompt.
 *
 * Browser dialogs are unstyled, block the whole tab, and on some browsers can
 * be suppressed entirely ("prevent this page from creating more dialogs"),
 * which silently kills a workflow mid-action.
 *
 * Usage — confirm:
 *   <ConfirmDialog open={x} title="..." message="..." onConfirm={fn} onCancel={fn} />
 *
 * Usage — prompt (requires text before confirming):
 *   <ConfirmDialog open={x} requireInput inputLabel="Reason" onConfirm={(text) => ...} />
 */
const TONES = {
  danger: {
    icon: AlertTriangle,
    iconClass: 'text-red-600',
    iconBg: 'bg-red-100',
    button: 'bg-red-600 hover:bg-red-700',
  },
  warning: {
    icon: AlertTriangle,
    iconClass: 'text-amber-600',
    iconBg: 'bg-amber-100',
    button: 'bg-amber-600 hover:bg-amber-700',
  },
  info: {
    icon: Info,
    iconClass: 'text-blue-600',
    iconBg: 'bg-blue-100',
    button: 'bg-blue-600 hover:bg-blue-700',
  },
  question: {
    icon: HelpCircle,
    iconClass: 'text-purple-600',
    iconBg: 'bg-purple-100',
    button: 'bg-purple-600 hover:bg-purple-700',
  },
};

const ConfirmDialog = ({
  open,
  title,
  message,
  details,
  tone = 'question',
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  requireInput = false,
  inputLabel = 'Reason',
  inputPlaceholder = '',
  busy = false,
  onConfirm,
  onCancel,
}) => {
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValue('');
      // Let the dialog paint before grabbing focus.
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape' && !busy) onCancel?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const theme = TONES[tone] || TONES.question;
  const Icon = theme.icon;
  const blocked = busy || (requireInput && !value.trim());

  const submit = (e) => {
    e.preventDefault();
    if (blocked) return;
    onConfirm?.(requireInput ? value.trim() : undefined);
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !busy) onCancel?.();
      }}
    >
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full" role="dialog" aria-modal="true">
        <form onSubmit={submit}>
          <div className="p-6">
            <div className="flex gap-4">
              <div className={`flex-shrink-0 w-11 h-11 rounded-full ${theme.iconBg} flex items-center justify-center`}>
                <Icon className={`w-6 h-6 ${theme.iconClass}`} />
              </div>
              <div className="flex-1 min-w-0">
                {title && <h3 className="text-lg font-bold text-gray-900 mb-1">{title}</h3>}
                {message && <p className="text-sm text-gray-600 whitespace-pre-line">{message}</p>}
                {details && (
                  <div className="mt-3 p-3 bg-gray-50 border rounded-lg text-sm text-gray-700 whitespace-pre-line">
                    {details}
                  </div>
                )}

                {requireInput && (
                  <div className="mt-4">
                    <label className="block text-sm font-semibold text-gray-700 mb-1">
                      {inputLabel} <span className="text-red-500">*</span>
                    </label>
                    <input
                      ref={inputRef}
                      type="text"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      placeholder={inputPlaceholder}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                      disabled={busy}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="px-6 py-4 bg-gray-50 border-t rounded-b-xl flex justify-end gap-3">
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-100 font-semibold disabled:opacity-50"
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              disabled={blocked}
              className={`px-4 py-2 text-white rounded-lg font-semibold disabled:opacity-50 ${theme.button}`}
            >
              {busy ? 'Working...' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmDialog;
